import React, { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { format, parseISO } from 'date-fns';
import { nl } from 'date-fns/locale';
import { toast } from 'sonner';
import { Plus } from 'lucide-react';
import SprekersLayout from '@/components/SprekersLayout';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { useAuth } from '@/context/AuthContext';
import { sprekersApi } from '../api';
import SectionHeader from '../components/SectionHeader';
import EngagementFormDialog from '../components/EngagementFormDialog';
import useSpeakerProfiles from '../hooks/useSpeakerProfiles';
import { ENGAGEMENT_STATUSES, findClassName, findLabel } from '../labels';
import type { EngagementStatus, SpeakingEngagement, SpeakingEngagementInput } from '../types';

export default function EngagementsPage() {
  const { hasCapability } = useAuth();
  const canManage = hasCapability('speakers_manage');
  const profiles = useSpeakerProfiles();

  const [engagements, setEngagements] = useState<SpeakingEngagement[]>([]);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState<EngagementStatus | 'all'>('all');
  const [dialogOpen, setDialogOpen] = useState(false);

  const fetchEngagements = async () => {
    try {
      const data = await sprekersApi.listEngagements();
      setEngagements(data);
    } catch (error) {
      toast.error('Kon spreekbeurten niet laden');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchEngagements();
  }, []);

  const speakerName = (speakerId: string) => profiles.find((p) => p.id === speakerId)?.display_name || 'Onbekend';

  const filteredEngagements = useMemo(
    () =>
      engagements
        .filter((e) => statusFilter === 'all' || e.status === statusFilter)
        .sort((a, b) => new Date(a.start_at).getTime() - new Date(b.start_at).getTime()),
    [engagements, statusFilter],
  );

  const handleCreate = async (data: SpeakingEngagementInput) => {
    try {
      await sprekersApi.createEngagement(data);
      toast.success('Spreekbeurt aangemaakt');
      fetchEngagements();
    } catch (error: any) {
      toast.error(error.response?.data?.detail || 'Aanmaken mislukt');
      throw error;
    }
  };

  return (
    <SprekersLayout>
      <SectionHeader
        title="Spreekbeurten"
        description="Plan sprekers in en volg de status van hun voorbereiding."
        action={canManage && (
          <Button onClick={() => setDialogOpen(true)}>
            <Plus className="mr-2 h-4 w-4" />
            Nieuwe spreekbeurt
          </Button>
        )}
      />

      <div className="mb-4">
        <Select value={statusFilter} onValueChange={(value: EngagementStatus | 'all') => setStatusFilter(value)}>
          <SelectTrigger className="w-56"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Alle statussen</SelectItem>
            {ENGAGEMENT_STATUSES.map((option) => (
              <SelectItem key={option.value} value={option.value}>{option.label}</SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      <Card>
        <CardContent className="p-0">
          {!loading && filteredEngagements.length === 0 && (
            <p className="p-6 text-sm text-slate-500">Geen spreekbeurten gevonden.</p>
          )}
          <div className="divide-y divide-slate-100">
            {filteredEngagements.map((engagement) => (
              <Link
                key={engagement.id}
                to={`/sprekers/${engagement.id}`}
                className="flex items-center justify-between gap-4 p-4 hover:bg-slate-50"
              >
                <div className="min-w-0 flex-1">
                  <div className="flex flex-wrap items-center gap-2">
                    <p className="font-medium text-slate-900">{engagement.title || engagement.working_title || 'Zonder titel'}</p>
                    <Badge className={findClassName(ENGAGEMENT_STATUSES, engagement.status)}>
                      {findLabel(ENGAGEMENT_STATUSES, engagement.status)}
                    </Badge>
                  </div>
                  <p className="mt-1 text-sm text-slate-500">
                    {speakerName(engagement.speaker_id)} &middot;{' '}
                    {format(parseISO(engagement.start_at), "d MMMM yyyy 'om' HH:mm", { locale: nl })}
                    {engagement.theme ? ` · ${engagement.theme}` : ''}
                  </p>
                </div>
              </Link>
            ))}
          </div>
        </CardContent>
      </Card>

      <EngagementFormDialog open={dialogOpen} onOpenChange={setDialogOpen} engagement={null} onSubmit={handleCreate} />
    </SprekersLayout>
  );
}
